/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMantineColorScheme, useMantineTheme } from '@mantine/core'
import { UseQueryResult } from '@tanstack/react-query'
import type { ApexOptions } from 'apexcharts'
import en from 'apexcharts/dist/locales/en.json'
import pl from 'apexcharts/dist/locales/pl.json'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TChartLabel, TMeasurementItem } from 'shared/types'

// Can't type ApexCharts :D
type TUseAreaChartOptions = {
  disableToolbar?: boolean
  measurements: UseQueryResult<TMeasurementItem[]>
  labels?: UseQueryResult<TChartLabel[]>
  title: string
  deviceId: string
  yaxisMinValue?: number
  yaxisMaxValue?: number
  synchronize?: boolean
  events?: {
    animationEnd?(chart: any, options?: any): void
    beforeMount?(chart: any, options?: any): void
    mounted?(chart: any, options?: any): void
    updated?(chart: any, options?: any): void
    mouseMove?(e: any, chart?: any, options?: any): void
    mouseLeave?(e: any, chart?: any, options?: any): void
    click?(e: any, chart?: any, options?: any): void
    xAxisLabelClick?(e: any, chart?: any, options?: any): void
    legendClick?(chart: any, seriesIndex?: number, options?: any): void
    markerClick?(e: any, chart?: any, options?: any): void
    selection?(chart: any, options?: any): void
    dataPointSelection?(e: any, chart?: any, options?: any): void
    dataPointMouseEnter?(e: any, chart?: any, options?: any): void
    dataPointMouseLeave?(e: any, chart?: any, options?: any): void
    beforeZoom?(chart: any, options?: any): void
    beforeResetZoom?(chart: any, options?: any): void
    zoomed?(chart: any, options?: any): void
    scrolled?(chart: any, options?: any): void
    brushScrolled?(chart: any, options?: any): void
  }
}

export const useAreaChartOptions = ({
  measurements: { data, isSuccess },
  labels,
  title,
  events,
  deviceId,
  disableToolbar,
  yaxisMinValue,
  yaxisMaxValue,
  synchronize,
}: TUseAreaChartOptions) => {
  const { t } = useTranslation()
  const chartTitle = t(`indicator.${title}`)
  const unit = Array.isArray(data) && data.length > 0 ? data[0].unit : null
  const { i18n } = useTranslation()
  const code = i18n.language
  const { colorScheme } = useMantineColorScheme()
  const theme = useMantineTheme()
  const apexTooltipTheme = colorScheme === 'dark' ? 'dark' : 'light'

  const series = useMemo(
    () =>
      data && isSuccess
        ? [
            {
              name: chartTitle,
              data: data?.map(item => ({
                x: dayjs(item.date).format(),
                y: item.value,
              })),
            },
          ]
        : [],
    [data, isSuccess, chartTitle],
  )

  const annotations = useMemo(
    () =>
      labels && labels.isSuccess
        ? [
            ...labels.data.map(label => ({
              x: dayjs(label.dateFrom).valueOf(),
              x2: dayjs(label.dateTo).valueOf(),
              fillColor: label?.hexColor ?? '#ef7171',
              label: {
                text: label?.title,
                textAnchor: 'start',
                style: {
                  background:
                    colorScheme === 'light' ? '#fff' : theme.colors.dark[6],
                },
              },
            })),
          ]
        : [],
    [labels?.data, labels?.isSuccess, colorScheme],
  )

  const options: ApexOptions = useMemo(
    () => ({
      annotations: {
        xaxis: annotations,
      },
      chart: {
        id: `${deviceId}-${title}`,
        group: synchronize ? deviceId : undefined,
        locales: [pl, en],
        defaultLocale: code.substring(0, 2),
        foreColor: colorScheme === 'dark' ? theme.white : theme.black,
        height: 350,
        zoom: {
          enabled: true,
          autoScaleYaxis: true,
        },
        fontFamily: 'Poppins, sans-serif',
        selection: {
          enabled: true,
          type: 'x',
          fill: {
            color: colorScheme === 'light' ? '#24292e' : theme.colors.gray[3],
          },
        },
        forecastDataPoints: {
          count: 50,
          fillOpacity: 0.5,
          strokeWidth: undefined,
          dashArray: 4,
        },

        toolbar: {
          show: !disableToolbar,
          autoSelected: 'zoom',
          // export: {
          //   csv: {
          //     filename: `${deviceId}-${title}`,
          //     columnDelimiter: ';',
          //     headerCategory: t('generic.date'),
          //     headerValue: chartTitle,
          //     dateFormatter(timestamp) {
          //       return dayjs(timestamp).format('DD.MM.YYYY HH:mm:ss')
          //     },
          //   },
          //   svg: {
          //     filename: `${deviceId}-${title}`,
          //   },
          //   png: {
          //     filename: `${deviceId}-${title}`,
          //   },
          // },
          tools: {
            download: false,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: false,
            reset: false,
            customIcons: [
              {
                icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/></svg>',
                index: 4,
                title: 'Download CSV',
                class: 'custom-csv-icon',
                click: function (chart: any) {
                  const seriesData = chart.w.globals.initialSeries[0].data
                  let csvContent = `${t('generic.date')};${chartTitle}\n`

                  seriesData.forEach((point: any) => {
                    const date = dayjs(point.x).format('DD.MM.YYYY HH:mm:ss')
                    const value = point.y
                    const formattedValue =
                      value !== null && value !== undefined
                        ? value.toFixed(2).replace('.', ',')
                        : ''
                    csvContent += `${date};${formattedValue}\n`
                  })

                  // Add BOM for proper Polish characters display in Excel
                  const BOM = '\uFEFF'
                  const blob = new Blob([BOM + csvContent], {
                    type: 'text/csv;charset=utf-8;',
                  })
                  const link = document.createElement('a')
                  const url = URL.createObjectURL(blob)
                  link.setAttribute('href', url)
                  link.setAttribute('download', `${deviceId}-${title}.csv`)
                  link.style.visibility = 'hidden'
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                },
              },
            ],
          },
        },
        ...(events && { events }),
      },
      noData: {
        text: t('generic.noData')!,
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'straight',
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: unit?.symbol,
        },
        min: yaxisMinValue,
        max: yaxisMaxValue,
        labels: {
          minWidth: 40,
          formatter: val => (val !== null ? val.toFixed(2) : val),
        },
      },
      tooltip: {
        x: {
          show: true,
          format: 'dd.MM HH:mm',
        },
        y: {
          formatter: function (value) {
            return `${value !== null ? value.toFixed(2) : ''} ${
              unit ? unit?.symbol : ''
            }`
          },
        },
        theme: apexTooltipTheme,
      },
      colors: [
        colorScheme === 'dark' ? theme.colors.brand[5] : theme.colors.brand[6],
      ],
    }),
    [colorScheme, apexTooltipTheme, unit, annotations, disableToolbar],
  )

  return { options, series }
}
